import { CustomDatePicker } from '@/lib/components/core/custom-datepicker/CustomDatepicker';
import { useGetWatercoachRegistratie, useUpsertWatercoachRegistratie } from '@/lib/queries/config-api/watercoach';
import { WatercoachRegistratieInput } from '@/lib/types/watercoach';
import { App, Button, Card, Descriptions, Divider, Form, List, Spin, Switch, Typography } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import './page.scss';

const { Text } = Typography;

interface WatercoachRegistratieFormValues extends WatercoachRegistratieInput {
  bezoekGeregistreerd?: boolean;
  adviesOpgevolgd?: boolean;
}

const toDateOnly = (value?: string): string | undefined => {
  if (!value) {
    return undefined;
  }

  return dayjs(value).format('YYYY-MM-DD');
};

export const WatercoachEditPage: React.FunctionComponent = () => {
  const { t } = useTranslation('core');
  const { message } = App.useApp();
  const deelnemerId = Number(useParams().id);
  const [registratieForm] = Form.useForm<WatercoachRegistratieFormValues>();
  const { data, isFetching } = useGetWatercoachRegistratie(deelnemerId);
  const upsertRegistratie = useUpsertWatercoachRegistratie();
  const bezoekGeregistreerd = Form.useWatch('bezoekGeregistreerd', registratieForm);
  const adviesOpgevolgd = Form.useWatch('adviesOpgevolgd', registratieForm);

  useEffect(() => {
    registratieForm.setFieldsValue({
      deelnemerId,
      benaderd: data?.laatsteRegistratie?.benaderd ?? false,
      bezoekGeregistreerd: !!data?.laatsteRegistratie?.bezoekDatum,
      bezoekDatum: toDateOnly(data?.laatsteRegistratie?.bezoekDatum),
      adviesOpgevolgd: !!data?.laatsteRegistratie?.adviesOpgevolgdDatum,
      adviesOpgevolgdDatum: toDateOnly(data?.laatsteRegistratie?.adviesOpgevolgdDatum),
    });
  }, [data, deelnemerId, registratieForm]);

  useEffect(() => {
    if (bezoekGeregistreerd === false) {
      registratieForm.setFieldsValue({ bezoekDatum: undefined });
    }
  }, [bezoekGeregistreerd, registratieForm]);

  useEffect(() => {
    if (adviesOpgevolgd === false) {
      registratieForm.setFieldsValue({ adviesOpgevolgdDatum: undefined });
    }
  }, [adviesOpgevolgd, registratieForm]);

  if (isFetching) {
    return <Spin size="large" />;
  }

  return (
    <Card
      title={t('watercoach.title')}
      extra={
        <Button type="primary" htmlType="submit" form="watercoachEditForm" loading={upsertRegistratie.isPending}>
          {t('table.opslaan')}
        </Button>
      }>
      <Descriptions column={1} style={{ marginBottom: 16 }}>
        <Descriptions.Item label={t('deelnemer.uONummer')}>{data?.uoNummer ?? '-'}</Descriptions.Item>
        <Descriptions.Item label={t('deelnemer.bedrijfsnaam')}>{data?.bedrijfsnaam ?? '-'}</Descriptions.Item>
        <Descriptions.Item label={t('deelnemer.contactPersoon')}>{data?.contactPersoon ?? '-'}</Descriptions.Item>
        <Descriptions.Item label={t('deelnemer.gemeente')}>{data?.gemeente ?? '-'}</Descriptions.Item>
        <Descriptions.Item label={t('deelnemer.waterschap')}>{data?.waterschap ?? '-'}</Descriptions.Item>
        <Descriptions.Item label={t('watercoach.vestigingsadres')}>{data?.vestigingsAdres ?? '-'}</Descriptions.Item>
      </Descriptions>

      <Form
        id="watercoachEditForm"
        form={registratieForm}
        layout="vertical"
        onFinish={values => {
          const registratie: WatercoachRegistratieInput = {
            deelnemerId,
            benaderd: values.benaderd,
            bezoekDatum: values.bezoekGeregistreerd ? toDateOnly(values.bezoekDatum) : undefined,
            adviesOpgevolgdDatum: values.adviesOpgevolgd ? toDateOnly(values.adviesOpgevolgdDatum) : undefined,
          };

          upsertRegistratie.mutate(registratie, {
            onSuccess: () => message.success(t('watercoach.saveSuccess')),
          });
        }}>
        <Divider></Divider>
        <div className="watercoach-question-grid">
          <QuestionToggle label={t('watercoach.benaderd')} name="benaderd" />
          <QuestionToggle label={t('watercoach.bezochtVraag')} name="bezoekGeregistreerd" />
          {bezoekGeregistreerd && (
            <Form.Item label={t('watercoach.bezoekDatumLabel')} name="bezoekDatum" rules={[{ required: true }]} className="watercoach-date-picker">
              <CustomDatePicker style={{ width: '100%' }} picker="date" />
            </Form.Item>
          )}

          <QuestionToggle label={t('watercoach.adviesVraag')} name="adviesOpgevolgd" />
          <Form.Item
            label={t('watercoach.adviesDatumLabel')}
            name="adviesOpgevolgdDatum"
            dependencies={['bezoekDatum']}
            hidden={!adviesOpgevolgd}
            className="watercoach-date-picker"
            rules={[
              { required: !!adviesOpgevolgd },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const bezoekDatum: string | undefined = getFieldValue('bezoekDatum');
                  if (value && !bezoekDatum) {
                    return Promise.reject(new Error(t('watercoach.bezoekDatumRequired')));
                  }

                  if (value && bezoekDatum && dayjs(bezoekDatum).isAfter(dayjs(value), 'day')) {
                    return Promise.reject(new Error(t('watercoach.bezoekDatumBeforeAdviesDatum')));
                  }

                  return Promise.resolve();
                },
              }),
            ]}>
            <CustomDatePicker style={{ width: '100%' }} picker="date" />
          </Form.Item>
        </div>
      </Form>

      <div style={{ maxWidth: '50%', margin: '0 auto' }}>
        <Divider>{t('deelnemer.toelichtingWijzigingenTitel')}</Divider>
        <List
          style={{ textAlign: 'center' }}
          itemLayout="horizontal"
          dataSource={data?.toelichtingen ?? []}
          locale={{ emptyText: t('deelnemer.toelichtingenLeeg') }}
          renderItem={item => (
            <List.Item style={{ justifyContent: 'center' }}>
              <List.Item.Meta
                title={<Text style={{ textTransform: 'initial' }}>{t('watercoach.historyItemTitle')}</Text>}
                description={<span>{`${item.aangemaaktDoorGebruikerEmail} - ${new Date(item.aangemaaktOp).toLocaleDateString()}`}</span>}
              />
            </List.Item>
          )}
        />
      </div>
    </Card>
  );
};

const QuestionToggle: React.FunctionComponent<{ label: string; name: keyof WatercoachRegistratieFormValues }> = ({ label, name }) => {
  const { t } = useTranslation('core');

  return (
    <>
      <Form.Item className="watercoach-question-label">
        <span>{label}</span>
      </Form.Item>
      <Form.Item className="watercoach-question-label">
        <Form.Item name={name} valuePropName="checked" noStyle>
          <Switch checkedChildren={t('boolean.yes')} unCheckedChildren={t('boolean.no')} />
        </Form.Item>
      </Form.Item>
    </>
  );
};
